/* Alpine vehicle finder: complete application tuples, never keyword-based fitment. */
(() => {
  'use strict';
  if (customElements.get('alpine-vehicle-finder')) return;
  const KEY = 'alpine.vehicle.v1';
  const safe = value => typeof value === 'string' ? value : '';
  const matches = (app, car, upto = 4) => Number(car.year) >= app.from && Number(car.year) <= app.to &&
    (upto < 2 || app.make === car.make) && (upto < 3 || app.model === car.model) && (upto < 4 || app.engine === car.engine);
  const valid = app => app && Number.isInteger(app.from) && Number.isInteger(app.to) &&
    app.from >= 1900 && app.to <= 2100 && app.to >= app.from && ['make','model','engine'].every(k => safe(app[k]));
  class AlpineVehicleFinder extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.fields = ['year','make','model','engine'].map(k => this.querySelector(`[name="${k}"]`));
      this.form = this.querySelector('form'); this.status = this.querySelector('[data-status]');
      this.results = this.querySelector('[data-results]'); this.list = this.querySelector('[data-products]');
      this.category = this.querySelector('[name="category"]'); this.retry = this.querySelector('[data-retry]');
      this.submit = this.querySelector('[type="submit"]'); this.catalogHidden = new Map();
      this.fields.forEach((field, i) => field.addEventListener('change', () => this.change(i), {signal}));
      this.form.addEventListener('submit', event => {event.preventDefault(); this.show(true);}, {signal});
      this.category.addEventListener('change', () => this.renderCards(), {signal});
      this.querySelector('[data-clear]').addEventListener('click', () => this.clear(), {signal});
      this.retry.addEventListener('click', () => this.load(), {signal});
      this.querySelector('[data-controls]').hidden = false;
      this.load();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null; this.loading = false; this.restoreCatalog();
    }
    text(key) {return this.dataset[key] || '';}
    selection() {return Object.fromEntries(this.fields.map(field => [field.name,field.value]));}
    options(field, values, saved = '') {
      field.replaceChildren(new Option(field.dataset.placeholder || '', ''));
      values.forEach(value => field.add(new Option(String(value),String(value))));
      field.disabled = !values.length; field.value = values.map(String).includes(String(saved)) ? String(saved) : '';
    }
    async page(pageNumber, signal) {
      const url = new URL(this.dataset.endpoint, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid endpoint');
      url.searchParams.set('view','alpine-fitment'); url.searchParams.set('page',String(pageNumber));
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id',preview);
      const request = new AbortController();
      const cancel = () => request.abort(); signal.addEventListener('abort',cancel,{once:true});
      const timeout = setTimeout(cancel,12000);
      try {
        const response = await fetch(url,{signal:request.signal,credentials:'same-origin',headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error('Fitment request failed');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const node = doc.querySelector('script[data-alpine-fitment-feed]');
        if (!node) throw new Error('Fitment feed unavailable');
        const data = JSON.parse(node.textContent);
        if (data.version !== 1 || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100 || data.page !== pageNumber || !Array.isArray(data.products)) throw new Error('Invalid fitment feed');
        return data;
      } finally {clearTimeout(timeout); signal.removeEventListener('abort',cancel);}
    }
    async load() {
      if (this.loading || !this.controller) return;
      this.loading = true; this.retry.hidden = true; this.submit.disabled = true;
      this.fields.forEach(field => field.disabled = true); this.status.textContent = this.text('loading');
      try {
        const signal = this.controller.signal; const first = await this.page(1,signal); const pages = [first];
        let nextPage = 2;
        const worker = async () => {while (nextPage <= first.pages) {const n = nextPage++; pages.push(await this.page(n,signal));}};
        await Promise.all(Array.from({length:Math.min(3,Math.max(0,first.pages-1))},worker));
        if (signal.aborted || !this.isConnected) return;
        const unique = new Map();
        pages.forEach(page => page.products.forEach(product => {
          if (!product || !safe(product.id) || !safe(product.title) || !Array.isArray(product.applications)) return;
          const url = new URL(product.url,location.origin);
          if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
          const apps = product.applications.filter(valid);
          if (apps.length) unique.set(product.id,{...product,url:url.pathname+url.search,applications:apps});
        }));
        this.products = [...unique.values()]; this.apps = this.products.flatMap(product => product.applications);
        const years = new Set(); this.apps.forEach(app => {for(let y=app.from;y<=app.to;y++) years.add(y);});
        this.options(this.fields[0],[...years].sort((a,b)=>b-a));
        for(let i=1;i<4;i++) this.options(this.fields[i],[]);
        this.status.textContent = this.apps.length ? this.text('ready') : this.text('empty');
        const coverage=this.querySelector('[data-coverage]'); if (coverage) coverage.textContent=this.text('coverage').replace('{count}',String(this.products.length));
        let saved = null; try {saved=JSON.parse(sessionStorage.getItem(KEY));} catch (_) {}
        if (saved && typeof saved === 'object') {
          for(let i=0;i<4;i++) {
            if(i>0) this.populate(i);
            const value = safe(saved[this.fields[i].name]);
            if([...this.fields[i].options].some(o=>o.value===value)) this.fields[i].value=value;
            else break;
          }
          if(this.complete()) this.show(false);
        }
        this.submit.disabled = !this.complete();
      } catch(error) {
        if (this.isConnected && !this.controller?.signal.aborted) {
          this.status.textContent=this.text('error'); this.retry.hidden=false;
          this.results.hidden=true; this.restoreCatalog();
        }
      } finally {this.loading=false;}
    }
    populate(index) {
      const car=this.selection(); const key=this.fields[index].name;
      const values=[...new Set(this.apps.filter(app=>matches(app,car,index)).map(app=>app[key]))].sort((a,b)=>a.localeCompare(b));
      this.options(this.fields[index],values);
    }
    change(index) {
      this.results.hidden=true; this.restoreCatalog();
      for(let i=index+1;i<4;i++) this.options(this.fields[i],[]);
      if(index<3 && this.fields[index].value) this.populate(index+1);
      if(index===2 && this.fields[3].options.length===2) this.fields[3].selectedIndex=1;
      this.submit.disabled=!this.complete();
      try {sessionStorage.removeItem(KEY);} catch (_) {}
      this.status.textContent=this.text('ready');
    }
    complete() {return this.fields.every(field=>field.value) && this.apps?.some(app=>matches(app,this.selection()));}
    show(focus) {
      if(!this.complete()) return;
      const car=this.selection();
      this.selectedProducts=this.products.map(product=>({...product,matching:product.applications.filter(app=>matches(app,car))})).filter(product=>product.matching.length);
      this.querySelector('[data-vehicle]').textContent=Object.values(car).join(' · ');
      const categories=[...new Set(this.selectedProducts.map(product=>product.category).filter(Boolean))].sort();
      this.options(this.category,categories); this.category.disabled=false;
      this.results.hidden=false; this.hideCatalog(); this.renderCards();
      try {sessionStorage.setItem(KEY,JSON.stringify(car));} catch (_) {}
      if(focus) this.querySelector('[data-vehicle]').focus({preventScroll:true});
    }
    hideCatalog() {
      const grid=document.querySelector('#ProductGridContainer');
      const elements=[grid?.closest('.shopify-section'),...document.querySelectorAll('.alpine-shop-section')];
      elements.filter(Boolean).forEach(element=>{if(!this.catalogHidden.has(element))this.catalogHidden.set(element,element.hidden);element.hidden=true;});
    }
    restoreCatalog() {this.catalogHidden?.forEach((hidden,element)=>element.hidden=hidden);this.catalogHidden?.clear();}
    renderCards() {
      const products=this.selectedProducts.filter(product=>!this.category.value || product.category===this.category.value);
      this.list.replaceChildren();
      products.forEach(product=>{
        const item=document.createElement('li'); item.className='alpine-vf__card';
        const link=document.createElement('a'); link.href=product.url;
        if(safe(product.image)) {
          const imageURL=new URL(product.image,location.origin);
          if(imageURL.protocol==='https:') {const img=document.createElement('img');img.src=imageURL.href;img.alt='';img.width=240;img.height=160;img.loading='lazy';link.append(img);}
        }
        const title=document.createElement('h3');title.textContent=product.title;link.append(title);item.append(link);
        const price=document.createElement('p');price.className='alpine-vf__price';price.textContent=safe(product.price);item.append(price);
        const availability=document.createElement('p');availability.className='alpine-vf__availability';availability.textContent=product.available?this.text('orderable'):this.text('unavailable');item.append(availability);
        const notes=document.createElement('p');notes.className='alpine-vf__notes';notes.textContent=[...new Set(product.matching.map(app=>safe(app.notes)).filter(Boolean))].join(' ');item.append(notes);
        const action=document.createElement('a');action.href=product.url;action.className='alpine-vf__view';action.textContent=this.text('view');item.append(action);
        this.list.append(item);
      });
      this.status.textContent=this.text('count').replace('{count}',String(products.length));
    }
    clear() {
      if(!this.apps) return;
      this.fields[0].value='';for(let i=1;i<4;i++)this.options(this.fields[i],[]);
      this.submit.disabled=true;this.results.hidden=true;this.list.replaceChildren();this.restoreCatalog();
      try {sessionStorage.removeItem(KEY);} catch (_) {}
      this.status.textContent=this.text('ready');this.fields[0].focus();
    }
  }
  customElements.define('alpine-vehicle-finder',AlpineVehicleFinder);
})();
